import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  CardHeader, 
  Grid, 
  Avatar, 
  Button, 
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  AccountBalance as BankIcon,
  Security as SecurityIcon,
  DirectionsCar as CarIcon
} from '@mui/icons-material';

// Import static data
import { employeesData } from '../../Data/Kontak/Employee';

const ViewEmployeeStatic = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    // Find employee by ID from static data
    const foundEmployee = employeesData.find(emp => emp.id === parseInt(id));
    setEmployee(foundEmployee);
  }, [id]);

  if (!employee) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6">Karyawan tidak ditemukan</Typography>
      </Box>
    );
  }

  const statusColor = employee.personalData.active_status === 1 ? 'success' : 'error';
  const statusText = employee.personalData.active_status === 1 ? 'Active' : 'Inactive';
  const genderIcon = employee.personalData.gender === 'L' ? '♂️' : '♀️';

  return (
    <Box sx={{ p: 2 }} className="animate-fade-in">
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/kontak/employee')}
            sx={{ 
              bgcolor: 'grey.100',
              '&:hover': { bgcolor: 'grey.200' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Detail Karyawan
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/kontak/employee/edit-static/${employee.id}`)}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Edit Karyawan
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Personal Data Card */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 'fit-content', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonIcon />
                </Avatar>
              }
              title="Data Pribadi"
              titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
              sx={{ bgcolor: 'primary.50', color: 'text.primary' }}
            />
            <CardContent sx={{ pt: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar
                  src={employee.personalData.photo}
                  alt={employee.personalData.employee_name}
                  sx={{ width: 120, height: 120, mb: 2, border: '4px solid', borderColor: 'primary.light' }}
                >
                  {!employee.personalData.photo && <PersonIcon sx={{ fontSize: 60 }} />}
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center', mb: 1 }}>
                  {employee.personalData.employee_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
                  ID: {employee.personalData.employee_code}
                </Typography>
                <Chip 
                  label={statusText} 
                  color={statusColor}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon><CalendarIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Tanggal Lahir"
                    secondary={new Date(employee.personalData.birth_date).toLocaleDateString('id-ID')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Jenis Kelamin"
                    secondary={`${genderIcon} ${employee.personalData.gender === 'L' ? 'Laki-laki' : 'Perempuan'}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Telepon"
                    secondary={employee.contact.mobile_phone}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Email"
                    secondary={employee.contact.corporate_email}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><LocationIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Tempat Lahir"
                    secondary={employee.personalData.birth_place}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={3}>
            {/* Personal Information Detail */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <PersonIcon />
                    </Avatar>
                  }
                  title="Informasi Pribadi Detail"
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  sx={{ bgcolor: 'info.50', color: 'text.primary' }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    {[
                      { label: 'Employee ID', value: employee.personalData.employee_id, icon: <BusinessIcon /> },
                      { label: 'Status Kepegawaian', value: employee.personalData.employment_status, icon: <WorkIcon /> },
                      { label: 'Tanggal Seniority', value: new Date(employee.personalData.seniority_date).toLocaleDateString('id-ID'), icon: <CalendarIcon /> },
                      { label: 'Status Pernikahan', value: employee.personalData.marital_status, icon: <PersonIcon /> },
                      { label: 'Agama', value: employee.personalData.religion, icon: <PersonIcon /> },
                      { label: 'Nama Gadis Ibu', value: employee.personalData.mother_maiden_name || '-', icon: <PersonIcon /> },
                    ].map(({ label, value, icon }, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          background: 'rgba(33, 150, 243, 0.05)',
                          border: '1px solid rgba(33, 150, 243, 0.1)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {React.cloneElement(icon, { sx: { color: 'info.main', fontSize: 20, mr: 1 } })}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                              {label}
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {value || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Work Information */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <WorkIcon />
                    </Avatar>
                  }
                  title="Info Pekerjaan"
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  sx={{ bgcolor: 'success.50', color: 'text.primary' }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    {[
                      { label: 'Unit Bisnis', value: employee.workInfo.business_unit, icon: <BusinessIcon /> },
                      { label: 'Cabang', value: employee.workInfo.branch_name, icon: <BusinessIcon /> },
                      { label: 'Departemen', value: employee.workInfo.department, icon: <WorkIcon /> },
                      { label: 'Posisi', value: employee.workInfo.position, icon: <WorkIcon /> },
                      { label: 'Grade', value: employee.workInfo.grade, icon: <WorkIcon /> },
                      { label: 'Grup Karyawan', value: employee.workInfo.employee_group, icon: <BusinessIcon /> },
                    ].map(({ label, value, icon }, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          background: 'rgba(76, 175, 80, 0.05)',
                          border: '1px solid rgba(76, 175, 80, 0.1)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {React.cloneElement(icon, { sx: { color: 'success.main', fontSize: 20, mr: 1 } })}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {label}
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {value || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Education & Address */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                borderRadius: 3,
                height: '100%'
              }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <SchoolIcon />
                    </Avatar>
                  }
                  title="Pendidikan"
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  sx={{ bgcolor: 'secondary.50', color: 'text.primary' }}
                />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main', mb: 0.5 }}>
                      Tingkat Pendidikan
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.workInfo.education}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main', mb: 0.5 }}>
                      Universitas
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.workInfo.education_group}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main', mb: 0.5 }}>
                      Jurusan
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.workInfo.university}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                borderRadius: 3,
                height: '100%'
              }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <LocationIcon />
                    </Avatar>
                  }
                  title="Alamat"
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  sx={{ bgcolor: 'warning.50', color: 'text.primary' }}
                />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
                      Alamat Lengkap
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', wordWrap: 'break-word' }}>
                      {employee.address.street}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
                      Kota, Provinsi
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.address.city}, {employee.address.province}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
                      RT/RW
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.address.rt}/{employee.address.rw}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Benefit Information */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'error.main' }}>
                      <BankIcon />
                    </Avatar>
                  }
                  title="Benefit"
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  sx={{ bgcolor: 'error.50', color: 'text.primary' }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    {[
                      { label: 'BPJS Ketenagakerjaan', value: employee.benefit.lbpjs_ketenagakerjaan_no, icon: <BankIcon /> },
                      { label: 'BPJS Kesehatan', value: employee.benefit.lbpjs_kesehatan_no || '-', icon: <BankIcon /> },
                      { label: 'BPJS Pensiun', value: employee.benefit.lbpjs_pensiun_no, icon: <BankIcon /> },
                      { label: 'Asuransi Perusahaan', value: employee.benefit.company_insurance_no, icon: <BankIcon /> },
                      { label: 'Koperasi', value: employee.benefit.lkoperasi ? 'Ya' : 'Tidak', icon: <BankIcon /> },
                      { label: 'SPSI', value: employee.benefit.lspsi ? 'Ya' : 'Tidak', icon: <BankIcon /> },
                    ].map(({ label, value, icon }, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          background: 'rgba(244, 67, 54, 0.05)',
                          border: '1px solid rgba(244, 67, 54, 0.1)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {React.cloneElement(icon, { sx: { color: 'error.main', fontSize: 20, mr: 1 } })}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                              {label}
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {value || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Tax & Others */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                borderRadius: 3,
                height: '100%'
              }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'grey.600' }}>
                      <SecurityIcon />
                    </Avatar>
                  }
                  title="Tax"
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  sx={{ bgcolor: 'grey.100', color: 'text.primary' }}
                />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.600', mb: 0.5 }}>
                      Kategori Pajak
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.tax.tax_category}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.600', mb: 0.5 }}>
                      Tax ID
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.tax.tax_id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.600', mb: 0.5 }}>
                      Payroll Seniority
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {new Date(employee.tax.payroll_seniority).toLocaleDateString('id-ID')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                borderRadius: 3,
                height: '100%'
              }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'purple' }}>
                      <CarIcon />
                    </Avatar>
                  }
                  title="Others"
                  titleTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                  sx={{ bgcolor: 'purple.50', color: 'text.primary' }}
                />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'purple', mb: 0.5 }}>
                      Tipe Kantor
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.others.office_type}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'purple', mb: 0.5 }}>
                      Toll Fee
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      Rp {employee.others.toll_fee}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'purple', mb: 0.5 }}>
                      Scan Biodata
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {employee.others.scan_biodata ? 'Sudah' : 'Belum'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewEmployeeStatic;

